import { styles } from "@repo/helpers";
import { Button, ButtonSize, ButtonTypes, Col, Row, useAlertContext } from "@repo/ui";
import { useCallback, useEffect, useRef, useState } from "react";

import type { Props } from "./types";

import style from "./style.module.scss";

export function EditableGridPage<T>(props: Props<T>) {
	const {
		pendingChanges,
		items,
		itemsLoaded,
		newItem,
		readOnly,
		Grid,
		Editor,
		entityId,
		updateRoute,
		fullReadOnly,
		gridContainerClassName,
		newItemText = "New",
	} = props;
	const [firstLoaded, setFirstLoaded] = useState(false);
	const [selected, setSelected] = useState<any[]>([]);
	const { clearAlert, unsavedChangesAlert } = useAlertContext();
	const lastOpenedId = useRef<number | undefined>(undefined);

	useEffect(() => {
		if (!entityId && newItem) openNew();
	}, []);

	useEffect(() => {
		if (entityId && !selected.includes(entityId)) {
			setSelected([entityId]);
		} else if (!entityId && selected.includes(entityId)) {
			setSelected([]);
		}
	}, [entityId, selected]);

	useEffect(() => {
		if (!itemsLoaded || !items.any()) return;

		if (entityId && entityId !== lastOpenedId.current) {
			openItem(entityId);
		} else if (!firstLoaded) {
			if (!props.noAutoOpen) {
				const item = items.last();
				// Record the auto-opened id, otherwise the entityId change it causes re-runs this
				// effect and re-opens the raw item over whatever the editor has already loaded
				lastOpenedId.current = item[props.keyProperty];
				props.openItem(item);
				updateRoute(item[props.keyProperty]);
			}
			setFirstLoaded(true);
		}
	}, [entityId, itemsLoaded, items?.any()]);

	const openNew = useCallback(() => {
		clearAlert();
		updateRoute();
		newItem();
		setSelected([]);
	}, [clearAlert, newItem, updateRoute]);

	const newClicked = useCallback(() => {
		if (pendingChanges) unsavedChangesAlert(openNew);
		else openNew();
	}, [openNew, unsavedChangesAlert, pendingChanges]);

	const openItem = useCallback(
		(id: number) => {
			const o = items.get(id);
			if (!o) return;
			lastOpenedId.current = id;
			updateRoute(id);
			props.openItem(o);
			clearAlert();
		},
		[clearAlert, items, props.openItem, updateRoute],
	);

	const rowClicked = useCallback(
		(id: number) => {
			if (id === entityId) return;
			const onConfirm = () => {
				openItem(id);
			};
			if (pendingChanges) unsavedChangesAlert(onConfirm);
			else {
				onConfirm();
			}
		},
		[openItem, unsavedChangesAlert, pendingChanges, entityId],
	);

	return (
		<section id={props.id} className={styles(style.gridPageContainer, gridContainerClassName)}>
			<Row>
				{newItem && !readOnly && !fullReadOnly ? (
					<div className={style.buttonContainer}>
						<div />
						<Button types={[ButtonTypes.Primary]} onClick={newClicked} size={ButtonSize.large}>
							{newItemText}
						</Button>
					</div>
				) : null}
				<Col sm={12}>
					<Grid onClickRow={rowClicked} selected={selected} keyProperty={props.keyProperty} />
				</Col>
			</Row>
			<hr />
			<Editor readOnly={props.readOnly} />
		</section>
	);
}
